import { useDisclosure } from '@mantine/hooks';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Button, Icon, Tab } from '@feathr/components';
import { SessionCacheProvider, useFundraiser } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';

import Page from '../Page';
import CreateFormModal from './CreateFormModal';
import FundraisingDashboard from './FundraisingDashboard';
import ExportDonationsButton from './FundraisingDashboard/ExportDonationsButton';
import FundraisingSettings from './FundraisingSettings';
import { useFundraiseTracking } from './hooks';

function FundraisePage(): JSX.Element {
  const { t } = useTranslation();
  const { DonationForms } = useStore();
  const location = useLocation();
  const [hasIntegration, setHasIntegration] = useState<boolean | undefined>(undefined);
  const { state } = useFundraiser();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'settings' | undefined>(undefined);
  const [isCreateFormModalOpen, { open: openCreateFormModal, close: closeCreateFormModal }] =
    useDisclosure(false);

  useFundraiseTracking(state);

  useEffect(() => {
    function getHasIntegration(): boolean {
      return state === 'connected';
    }

    const params = new URLSearchParams(location.search);
    const shouldOpenCRMModal = params.get('modal') === 'crm';
    const tabParam = params.get('tab');

    if (getHasIntegration()) {
      setHasIntegration(true);
      // Use tab param if provided, otherwise check for modal, otherwise default to dashboard
      setActiveTab(tabParam === 'settings' || shouldOpenCRMModal ? 'settings' : 'dashboard');
    } else {
      setHasIntegration(false);
      setActiveTab('settings');
    }
  }, [state, location.search]);

  function handleClickDashboardTab(): void {
    setActiveTab('dashboard');
  }

  function handleClickSettingsTab(): void {
    setActiveTab('settings');
  }

  // Only show tabs if set up is complete.
  const tabs = hasIntegration
    ? [
        <Tab
          active={activeTab === 'dashboard'}
          key={'dashboard'}
          onClick={handleClickDashboardTab}
          title={'Dashboard'}
        />,
        <Tab
          active={activeTab === 'settings'}
          key={'settings'}
          onClick={handleClickSettingsTab}
          title={'Settings'}
        />,
      ]
    : undefined;

  return (
    <SessionCacheProvider>
      <Page
        actions={
          hasIntegration ? (
            <>
              <ExportDonationsButton />
              <Button onClick={openCreateFormModal} prefix={<Icon.Add />} type={'primary'}>
                {t('Create Form')}
              </Button>
            </>
          ) : undefined
        }
        collapseHeader={true}
        tabs={hasIntegration ? tabs : []}
        title={t('Fundraising')}
      >
        {activeTab === 'dashboard' && <FundraisingDashboard />}
        {activeTab === 'settings' && <FundraisingSettings />}
      </Page>
      {isCreateFormModalOpen && (
        <CreateFormModal
          // TODO: Don't pass defaults once the backend is updated to set defaults
          form={DonationForms.create({
            // @ts-ignore will be removed once the backend is updated to set defaults
            config: { fields: { 'donation-amount': { properties: { options: [] } } } },
          })}
          onClose={closeCreateFormModal}
          opened={isCreateFormModalOpen}
        />
      )}
    </SessionCacheProvider>
  );
}

export default FundraisePage;
